import { useEffect, useRef } from "react";

import Box from "@mui/material/Box";

import AssistantChatBubble from "@pages/Technology/components/AssistanChatBubble";
import ChatWarning from "@pages/Technology/components/ChatWarning";
import RateSummary from "@pages/Technology/components/RateSummary";
import { IMessage } from "@pages/Technology/components/TechnologyMarketingSummary";
import UserChatBubble from "@pages/Technology/components/UserChatBubble";

import { useRateChatMessage } from "@queries/chat.query";

import { PopupActions } from "@store/popupStore";

interface ITechnologyMarketingSummaryMessagesProperties {
  isStreaming: boolean;
  messages: IMessage[];
  summaryId: string;
  technologyId: string;
}

const TechnologyMarketingSummaryMessages = ({
  isStreaming,
  messages,
  summaryId,
  technologyId,
}: ITechnologyMarketingSummaryMessagesProperties) => {
  const messagesEndReference = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndReference.current?.scrollIntoView({ behavior: "smooth" });
  };

  const { mutateAsync: rateChatMessage } = useRateChatMessage();

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleNegativeFeedbackClick = (message: IMessage) => {
    PopupActions.openModal({
      content: (
        <RateSummary
          message={message.content}
          messageId={message.id}
          promptType="system"
          summaryId={summaryId}
          technologyId={technologyId}
        />
      ),
      title: "Rate Summary",
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        maxWidth: 760,
        mx: "auto",
        py: 4,
      }}
    >
      <ChatWarning />
      {messages.map((message, index) => {
        if (message.role === "user") {
          return <UserChatBubble key={message.id} message={message.content} />;
        }
        if (message.role === "system" && message.isFile) {
          return <UserChatBubble isFile={message.isFile} key={message.id} message={message.fileName ?? ""} />;
        }
        return (
          <AssistantChatBubble
            handleNegativeFeedbackClick={() => handleNegativeFeedbackClick(message)}
            handlePositiveFeedbackClick={() => {
              void rateChatMessage({
                message: message.content,
                messageId: message.id,
                promptType: "system",
                rating: "positive",
                summaryId,
                technologyId,
              });
            }}
            isRated={message.isRated}
            isShowControls={index > 0 && !!summaryId && !isStreaming}
            isStreaming={isStreaming && index === messages.length - 1}
            key={message.id}
            message={message.content}
          />
        );
      })}
      <div ref={messagesEndReference} />
    </Box>
  );
};

export default TechnologyMarketingSummaryMessages;
